"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useBingMap = exports.callbackName = exports.BING_MAP_SCRIPT_URL = exports.BING_MAP_SCRIPT_ID = void 0;
var _checkoutUtils = require("@ori/checkout-utils");
var _react = require("react");
var _useClusteredPickupPointsQuery = require("../api/useClusteredPickupPointsQuery");
var _shared = require("../components/shared");
var _contexts = require("../contexts");
var _logger = require("../logger");
var _utils = require("../utils");
var _useForceRedraw = require("./useForceRedraw");
var _usePickupPointsSearchConfiguration = require("./usePickupPointsSearchConfiguration");
/* eslint-disable @typescript-eslint/no-unnecessary-condition -- wrong 3rd party typings */

const BING_MAP_SCRIPT_ID = exports.BING_MAP_SCRIPT_ID = 'bing-map-loader-script';
const BING_MAP_SCRIPT_URL = exports.BING_MAP_SCRIPT_URL = 'https://www.bing.com/api/maps/mapcontrol';
const hookName = 'useBingMap';
const callbackName = exports.callbackName = 'updateMap';
const useBingMap = props => {
  const {
    account: {
      credentials
    },
    activeFilter,
    currentLocation,
    defaultMapContainer,
    fallbackStartingLocation: initialLocation,
    instantSearchTerm,
    isMapVisible,
    initialZoom,
    minZoom,
    onMapInitialization,
    onPickupPointItemSelection,
    selectedPickupPoint
  } = props;
  const [isMapLoading, setIsMapLoading] = (0, _react.useState)(false);
  const [error, setError] = (0, _react.useState)(undefined);
  const {
    Maps
  } = window?.Microsoft ?? {};
  const map = (0, _react.useRef)(null);
  const mapContainer = (0, _react.useRef)(defaultMapContainer ?? null);
  // Holding the script load promise to be sure some calls are not duplicated
  // while some re-renders happen.
  const loadingScriptPromise = (0, _react.useRef)(undefined);
  // Storing the loaded script state in global config, as the script callback will be called
  // only once & the initialization of map wouldn't work when returning to the map tab
  // repeatedly.
  const {
    isExternalMapLoaded
  } = (0, _contexts.useCheckoutDeliveryContext)();
  const {
    mapMethods,
    setViewSettings
  } = (0, _usePickupPointsSearchConfiguration.usePickupPointsSearchConfiguration)();
  const isMapInitialized = Boolean(typeof Maps === 'object' && map.current);

  // Store initial map view options in refs. This can be overwritten
  // until the map gets initialized. When initializing, map view will be set once
  // to the settings stored here.
  const startupLocation = (0, _react.useRef)(initialLocation);
  const startupZoom = (0, _react.useRef)(initialZoom);

  // TO-DO: Let's get rid of this if possible.
  const redraw = (0, _useForceRedraw.useForceRedraw)();
  const boundsRect = map.current?.getBounds();
  const pickupPointsQueryOptions = {
    east: boundsRect?.getEast(),
    isActive: isMapVisible,
    filter: activeFilter,
    north: boundsRect?.getNorth(),
    searchTerm: instantSearchTerm,
    south: boundsRect?.getSouth(),
    west: boundsRect?.getWest(),
    zoom: map.current?.getZoom()
  };
  const {
    clusteredPickupPointsList,
    isClusteredPickupPointsListLoading
  } = (0, _useClusteredPickupPointsQuery.useClusteredPickupPointsQuery)(pickupPointsQueryOptions);
  (0, _logger.logDebug)(`${hookName} hook was called.\n\tProps:`, props, '\n\tLocal vars:', {
    isExternalMapLoaded,
    isMapInitialized,
    isMapLoading,
    isMapVisible,
    mapMethods,
    pickupPointsQueryOptions,
    startupLocation,
    startupZoom
  });
  const getViewBounds = (0, _react.useCallback)(() => {
    if (typeof Maps !== 'object' || !map.current) {
      (0, _logger.logWarning)(`${hookName}: getViewBounds() should be called after map has been initialized.`);
      return {
        east: 0,
        north: 0,
        south: 0,
        west: 0
      };
    }
    const bounds = map.current.getBounds();
    const east = bounds.getEast();
    const north = bounds.getNorth();
    const south = bounds.getSouth();
    const west = bounds.getWest();
    return {
      east,
      north,
      south,
      west
    };
  }, [Maps]);
  const getViewOptions = (0, _react.useCallback)(viewOptions => {
    const {
      location: rawLocation,
      zoom = map?.current?.getZoom() ?? startupZoom.current
    } = viewOptions ?? {};
    const currentCenter = map?.current?.getCenter();
    const location = rawLocation ?? (currentCenter ? {
      latitude: Number(currentCenter.latitude),
      longitude: Number(currentCenter.longitude)
    } : startupLocation.current);
    return {
      location,
      zoom
    };
  }, [map, startupLocation, startupZoom]);
  const updateView = (0, _react.useCallback)(viewOptions => {
    const {
      location,
      zoom
    } = getViewOptions(viewOptions);
    if (typeof Maps === 'object' && map.current) {
      // update map view immediately
      const center = new Maps.Location(location.latitude, location.longitude);
      const settings = {
        center,
        zoom
      };
      (0, _logger.logDebug)(`${hookName}: updating map view, options:`, settings);
      map.current.setView(settings);
      setViewSettings(getViewOptions());
      redraw();
    } else {
      // schedule updating map view for later
      startupZoom.current = zoom;
      startupLocation.current = location;
      (0, _logger.logDebug)(`${hookName}: updateView(): new view options stored:`, {
        location,
        zoom
      });
    }
  }, [Maps, getViewOptions, redraw, setViewSettings]);
  const setViewBounds = (0, _react.useCallback)((bounds, padding) => {
    if (typeof Maps !== 'object' || !map.current) {
      (0, _logger.logWarning)(`${hookName}: setViewBounds() should be called after map has been initialized.`);
      return;
    }
    (0, _logger.logDebug)(`${hookName}: setting map view to new bounds, bounds:`, bounds, ', padding:', padding);
    const {
      east,
      north,
      south,
      west
    } = bounds;
    const usedBounds = Maps.LocationRect.fromEdges(north, west, south, east);
    map.current.setView({
      bounds: usedBounds,
      padding
    });
  }, [Maps]);
  const getViewSettings = (0, _react.useCallback)(() => getViewOptions(), [getViewOptions]);

  // define exposed map methods
  const exposeMapMethods = (0, _react.useCallback)(() => {
    const result = {
      getViewBounds,
      getViewSettings,
      setLocationOrZoom: settings => updateView(settings),
      setViewBounds,
      zoomIn: () => updateView({
        zoom: (map?.current?.getZoom() ?? initialZoom) + 1
      }),
      zoomOut: () => updateView({
        zoom: (map?.current?.getZoom() ?? initialZoom) - 1
      })
    };
    mapMethods.current = result;
    (0, _logger.logDebug)(`${hookName}: exposeMapMethods() called, mapMethods:`, result);
  }, [getViewBounds, getViewSettings, initialZoom, mapMethods, setViewBounds, updateView]);
  const removePushpins = (0, _react.useCallback)(() => {
    if (!(typeof Maps === 'object' && map.current)) {
      return;
    }
    const l = map.current?.entities?.getLength() ?? 0;
    for (let i = l - 1; i >= 0; i--) {
      const pushpin = map.current?.entities.get(i);
      if (pushpin instanceof Maps.Pushpin) {
        map.current?.entities.removeAt(i);
      }
    }
  }, [Maps]);
  const updatePushpins = (0, _react.useCallback)(() => {
    if (!(typeof Maps === 'object' && map.current)) {
      return;
    }
    removePushpins(); // TO-DO: optimization without deleting all?
    (0, _logger.logDebug)(`${hookName}: updatePushpins() was called.\n\tcurrent pushpins data:`, clusteredPickupPointsList);
    clusteredPickupPointsList.forEach(({
      cluster: rawCluster,
      coordinates: {
        latitude,
        longitude
      },
      displayedClusterSize,
      isPickupPoint,
      pickupPoint: rawPickupPoint
    }) => {
      let icon;
      let isSelected;
      let clickHandler;
      if (isPickupPoint) {
        const pickupPoint = rawPickupPoint;
        const {
          deliveryChannelId,
          deliveryMethod,
          estimatedDeliveryDate,
          shippingFee
        } = pickupPoint;
        isSelected = deliveryChannelId === selectedPickupPoint?.deliveryChannelId;
        icon = (0, _shared.getPickupPointMapIconSvgString)(deliveryMethod, isSelected);
        clickHandler = () => {
          (0, _logger.logDebug)('Pickup point icon was clicked. deliveryChannelId', deliveryChannelId);
          onPickupPointItemSelection?.({
            ...pickupPoint,
            coordinates: {
              latitude,
              longitude
            },
            deliveryFee: shippingFee,
            deliveryMethod,
            estimatedDelivery: estimatedDeliveryDate
          });
        };
      } else {
        const cluster = rawCluster;
        const {
          lowerLeft: {
            latitude: south = 0,
            longitude: west = 0
          } = {},
          upperRight: {
            latitude: north = 0,
            longitude: east = 0
          } = {}
        } = cluster;
        isSelected = false;
        icon = (0, _shared.getClusterMapIconSvgString)(displayedClusterSize, isSelected);
        clickHandler = () => {
          (0, _logger.logDebug)('Cluster icon was clicked.');
          // deselect last selection, if any
          onPickupPointItemSelection?.(undefined);
          // zoom to cluster min - max coords
          setViewBounds({
            east,
            north,
            south,
            west
          }, Math.round(_shared.baseMarkerSize / 2));
        };
      }
      const {
        x,
        y
      } = (0, _shared.getAnchorCoordinates)(isPickupPoint, isSelected);
      const anchor = new Maps.Point(x, y);
      const item = new Maps.Pushpin(new Maps.Location(latitude, longitude), {
        anchor,
        icon
      });
      Maps.Events.addHandler(item, 'click', clickHandler);
      map.current?.entities.push(item);
    });
    // later added pushpin icon seems to have lower "z-index"
    if (currentLocation) {
      const {
        x,
        y
      } = (0, _shared.getAnchorCoordinates)(false, false);
      const anchor = new Maps.Point(x, y);
      const {
        latitude,
        longitude
      } = currentLocation;
      const item = new Maps.Pushpin(new Maps.Location(latitude, longitude), {
        anchor,
        icon: (0, _shared.getCurrentLocationIconSvgString)()
      });
      map.current?.entities.push(item);
    }
  }, [Maps, clusteredPickupPointsList, currentLocation, onPickupPointItemSelection, removePushpins, selectedPickupPoint?.deliveryChannelId, setViewBounds]);
  const updateMap = (0, _react.useCallback)(() => {
    // Works only when the Bing Map control script was already loaded & evaluated.
    if (typeof Maps !== 'object') {
      return;
    }

    // map control instance
    if (!map.current && mapContainer.current && credentials) {
      (0, _logger.logDebug)(`${hookName}: Initializing map instance ...`, '\n\twindow.Microsoft.Maps', Maps);
      map.current = new Maps.Map(mapContainer.current, {
        credentials,
        enableClickableLogo: false,
        minZoom,
        showDashboard: false,
        showLocateMeButton: false,
        showLogo: false,
        showMapTypeSelector: false,
        showScalebar: false,
        showTermsLink: false
      });
      const {
        latitude,
        longitude
      } = startupLocation.current ?? {};
      const viewOptions = {
        center: new Maps.Location(latitude, longitude),
        mapTypeId: Maps.MapTypeId.road,
        zoom: startupZoom.current
      };
      (0, _logger.logDebug)(`${hookName}: Initializing view, options:`, viewOptions);
      map.current.setView(viewOptions);
      Microsoft.Maps.Events.addHandler(map.current, 'click', () => {
        (0, _logger.logDebug)('Map was clicked.');
        // deselect last selection, if any
        onPickupPointItemSelection?.(undefined);
      });
      Microsoft.Maps.Events.addHandler(map.current, 'viewchangeend', () => {
        (0, _logger.logDebug)('Map view was changed.');
        redraw();
      });
      // Mark the scripts loaded state for next map tab activations.
      isExternalMapLoaded.current = true;
      // Expose the generic map methods
      exposeMapMethods();
      // Didn't find any event on Bing map once it is ready, so triggering redraw here
      // to get the pushpins displayed also immediatelly after load.
      redraw();
      onMapInitialization?.();
    }

    // Pushpins are rendered into HTML canvas element by the map instance.
    if (map.current) {
      updatePushpins();
    }
  }, [Maps, credentials, exposeMapMethods, isExternalMapLoaded, minZoom, onMapInitialization, onPickupPointItemSelection, redraw, updatePushpins]);
  const loadMap = (0, _react.useCallback)(async () => {
    if (loadingScriptPromise.current !== undefined) {
      return;
    }
    (0, _logger.logDebug)(`${hookName}: loadMap() was called.`);
    setIsMapLoading(true);
    loadingScriptPromise.current = (0, _utils.loadScript)(`${BING_MAP_SCRIPT_URL}?callback=${callbackName}`, BING_MAP_SCRIPT_ID);
    loadingScriptPromise.current.then(() => {
      (0, _logger.logDebug)(`${hookName}: Map control script was loaded.`);
    }).catch(initializationError => {
      (0, _checkoutUtils.showErrorNotification)();
      const message = String(initializationError);
      if (initializationError instanceof Error) {
        setError(initializationError);
      } else {
        const newError = new Error(message);
        setError(newError);
      }
    }).finally(() => {
      setIsMapLoading(false);
    });
  }, []);
  (0, _react.useEffect)(() => {
    if (isMapInitialized || isExternalMapLoaded.current) {
      updateMap();
    } else if (isMapVisible) {
      // Start loading external map control script when the map tab is visible for the 1st time.
      // That global callback seems to be necessary, otherwise, even when awaiting
      // the loadMap promise, the Bing Map control script raises multiple errors
      // before proper initialization, as it is loading & evaluating subsequently another stuff.
      // So the promise of loadMap() is not enough indication that everything is loaded.
      window[callbackName] = updateMap;
      void loadMap();
    }
  }, [isExternalMapLoaded, isMapInitialized, isMapVisible, loadMap, updateMap]);
  return {
    error,
    isClusteredPickupPointsListLoading,
    isLoading: isClusteredPickupPointsListLoading || isMapLoading,
    isMapLoading,
    map,
    mapContainer
  };
};
exports.useBingMap = useBingMap;